// ./src/hooks/useParkingSessions.ts
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  ParkingSession,
  ParkingSessionService,
  ParkingSessionInput,
  ParkingSessionFilter,
  PARKING_SESSION_STATUS,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { Pagination, SortItem } from "@/utils/api/commonApiTypes";
import { DEFAULT_PAGE_SIZE } from "@/config";

// ============================================
// Types
// ============================================

export interface UseParkingSessionsOptions {
  /** Auto-load data on mount (default: false) */
  autoLoad?: boolean;
  /** Initial filter to apply */
  initialFilter?: ParkingSessionFilter;
  /** Initial sort settings */
  initialSortBy?: SortItem[];
  /** Car ID to filter by */
  carId?: string;
  /** Page size for pagination */
  pageSize?: number;
}

export interface UseParkingSessionsReturn {
  // Data state
  sessions: ParkingSession[];
  selectedSession: ParkingSession | null;
  isLoading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Pagination state
  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  // Operations state
  isStarting: boolean;
  isUpdating: boolean;
  isEnding: boolean;
  isRemoving: boolean;
  removingId: string | null;

  // Filter and sort
  filter: ParkingSessionFilter;
  sortBy: SortItem[];

  // Core operations
  list: (
    filter?: ParkingSessionFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  loadMore: () => Promise<void>;
  get: (id: string) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  start: (params: ParkingSessionInput) => Promise<OpResult>;
  update: (id: string, params: ParkingSessionInput) => Promise<OpResult>;
  end: (
    id: string,
    params: {
      endTime?: string;
      finalPrice?: number;
      currency?: string;
      notes?: string;
      startTime?: string;
    }
  ) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;

  // Convenience methods
  refresh: () => Promise<OpResult>;
  select: (session: ParkingSession | null) => void;
  clearSelection: () => void;
  reset: () => void;

  // Derived data helpers
  activeSessions: ParkingSession[];
  completedSessions: ParkingSession[];
  activeSessionForCar: (carId: string) => ParkingSession | undefined;

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT_BY: SortItem[] = [
  { name: "startTime", order: "DESC" },
];

// ============================================
// Hook Implementation
// ============================================

export const useParkingSessions = (
  options: UseParkingSessionsOptions = {}
): UseParkingSessionsReturn => {
  const {
    autoLoad = false,
    initialFilter = {},
    initialSortBy = DEFAULT_SORT_BY,
    carId,
    pageSize: optionPageSize = DEFAULT_PAGE_SIZE,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // Use refs for option values to avoid dependency issues
  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const pageSizeRef = useRef(optionPageSize);
  const sortByRef = useRef<SortItem[]>(initialSortBy);
  const initialFilterRef = useRef<ParkingSessionFilter>(initialFilter);

  // Ref to track the last-queried filters for loadMore/refresh
  // so we never read stale `filter` state from a closure.
  const lastQueriedFilterRef = useRef<ParkingSessionFilter>(initialFilter);

  // Monotonically increasing call ID to detect stale list() responses.
  // When a new list() call starts, it increments this counter;
  // when the response arrives, it checks whether its ID still matches.
  const listCallIdRef = useRef(0);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = optionPageSize;
  }, [optionPageSize]);

  useEffect(() => {
    initialFilterRef.current = initialFilter;
  }, [initialFilter]);

  // Data state
  const [sessions, setSessions] = useState<ParkingSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ParkingSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Pagination state
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isStarting, setIsStarting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Filter and sort state
  const [filter, setFilter] = useState<ParkingSessionFilter>(() => ({
    ...initialFilter,
    ...(carId ? { carId: [carId] } : {}),
  }));
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSortBy);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && sessions.length === 0,
    [isLoaded, sessions.length]
  );

  // ==========================================
  // List Helpers
  // ==========================================

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: ParkingSession) => {
    setSessions((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === updatedItem.id);
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  // Helper function to remove item from list
  const removeFromList = useCallback((id: string) => {
    setSessions((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    // Invalidate any in-flight list() calls so their responses are ignored
    listCallIdRef.current++;

    setSessions([]);
    setSelectedSession(null);
    setLastError(null);
    setIsLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsStarting(false);
    setIsUpdating(false);
    setIsEnding(false);
    setIsRemoving(false);
    setRemovingId(null);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  // Get single session
  const get = useCallback(
    async (id: string): Promise<OpResult> => {
      setIsLoading(true);
      setLastError(null);

      try {
        const result = await ParkingSessionService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = Array.isArray(result.data)
            ? (result.data as ParkingSession[])[0]
            : (result.data as ParkingSession);
          setSelectedSession(data);
          updateOrAddInList(data);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load parking session"));
        setSelectedSession(null);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load parking session"));
        setSelectedSession(null);
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [updateOrAddInList]
  );

  /**
   * List sessions.
   * - shouldReset=true (default): replaces sessions array (page 1 load / filter change)
   * - shouldReset=false: appends to existing sessions (load more)
   *
   * Race-condition safe: each call gets a monotonic ID. If a newer call has
   * started by the time the response arrives, the stale response is discarded.
   * This prevents out-of-order responses from overwriting fresher data
   * (e.g. when the user changes filters rapidly).
   */
  const list = useCallback(
    async (
      filterOverride?: ParkingSessionFilter,
      paginationOverride?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = paginationOverride?.page || 1;
      const targetPageSize = paginationOverride?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setIsLoading(true);
        // Don't clear sessions here — keep stale data visible while loading.
        // The array is replaced atomically when the response arrives.
        setCurrentPage(1);
      }

      setLastError(null);

      // Store the queried filters/sort so loadMore can use them via refs
      if (filterOverride) {
        setFilter(filterOverride);
        lastQueriedFilterRef.current = filterOverride;
      }
      if (sortByOverride) setSortBy(sortByOverride);

      const effectiveFilter = filterOverride ?? lastQueriedFilterRef.current;
      const effectiveSortBy = sortByOverride ?? sortByRef.current;

      try {
        const result = await ParkingSessionService.list(
          effectiveFilter,
          { page: targetPage, pageSize: targetPageSize },
          effectiveSortBy
        );

        // Guard: if a newer list() call was initiated while we were waiting,
        // discard this response to avoid overwriting fresher data.
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK) {
          const newItems = (result.data as ParkingSession[]) || [];

          if (isAppending) {
            setSessions((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setSessions(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load parking sessions"));
        setIsLoaded(true);
        if (!isAppending) setSessions([]);
        return result;
      } catch (error) {
        // Guard stale responses for error path too
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load parking sessions"));
        setIsLoaded(true);
        if (!isAppending) setSessions([]);
        return result;
      } finally {
        // Only update loading flags if this call is still the latest.
        // A stale call should not flip loading to false while a newer
        // call is still in-flight.
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setIsLoading(false);
          }
        }
      }
    },
    []
  );

  // Get multiple sessions by IDs
  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        return new OpResult();
      }

      setIsLoading(true);
      setLastError(null);

      try {
        const result = await ParkingSessionService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as ParkingSession[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load parking sessions"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load parking sessions"));
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // ==========================================
  // Mutation Operations
  // ==========================================

  // Start a parking session
  const start = useCallback(
    async (params: ParkingSessionInput): Promise<OpResult> => {
      setIsStarting(true);
      setLastError(null);

      try {
        const result = await ParkingSessionService.start(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newSession = Array.isArray(result.data)
            ? (result.data as ParkingSession[])[0]
            : (result.data as ParkingSession);

          setSessions((prev) => [newSession, ...prev]);
          setSelectedSession(newSession);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to start parking session"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to start parking session"));
        return result;
      } finally {
        setIsStarting(false);
      }
    },
    []
  );

  // Update a parking session
  const update = useCallback(
    async (id: string, params: ParkingSessionInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Session ID is required for updating"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await ParkingSessionService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedSession = Array.isArray(result.data)
            ? (result.data as ParkingSession[])[0]
            : (result.data as ParkingSession);

          updateOrAddInList(updatedSession);
          setSelectedSession((prev) =>
            prev?.id === id ? updatedSession : prev
          );
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update parking session"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update parking session"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // End a parking session
  const end = useCallback(
    async (
      id: string,
      params: {
        endTime?: string;
        finalPrice?: number;
        currency?: string;
        notes?: string;
        startTime?: string;
      }
    ): Promise<OpResult> => {
      setIsEnding(true);
      setLastError(null);

      try {
        const result = await ParkingSessionService.end(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const endedSession = Array.isArray(result.data)
            ? (result.data as ParkingSession[])[0]
            : (result.data as ParkingSession);

          updateOrAddInList(endedSession);
          setSelectedSession((prev) =>
            prev?.id === id ? endedSession : prev
          );
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to end parking session"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to end parking session"));
        return result;
      } finally {
        setIsEnding(false);
      }
    },
    [updateOrAddInList]
  );

  // Remove a parking session
  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Session ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsRemoving(true);
      setRemovingId(id);
      setLastError(null);

      try {
        const result = await ParkingSessionService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setSelectedSession((prev) => (prev?.id === id ? null : prev));
          removeFromList(id);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove parking session"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove parking session"));
        return result;
      } finally {
        setIsRemoving(false);
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  // Load more sessions
  // Uses refs for filters and sort to avoid stale closures —
  // loadMore always reads whatever was last passed to list().
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPage + 1;
    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      sortByRef.current,
      false
    );
  }, [list, loadingMore, hasMore, currentPage]);

  // Refresh sessions (reset to page 1)
  // Uses the ref so it always picks up the last-queried filters,
  // not a potentially stale `filter` state snapshot.
  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list]);

  const select = useCallback((session: ParkingSession | null) => {
    setSelectedSession(session);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedSession(null);
  }, []);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  const activeSessions = useMemo(
    () => sessions.filter((s) => s.status === PARKING_SESSION_STATUS.ACTIVE),
    [sessions]
  );

  const completedSessions = useMemo(
    () => sessions.filter((s) => s.status === PARKING_SESSION_STATUS.COMPLETED),
    [sessions]
  );

  const activeSessionForCar = useCallback(
    (targetCarId: string): ParkingSession | undefined => {
      return sessions.find(
        (s) => s.carId === targetCarId && s.status === PARKING_SESSION_STATUS.ACTIVE
      );
    },
    [sessions]
  );

  // ==========================================
  // Effects
  // ==========================================

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFilterRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFilterRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      sessions,
      selectedSession,
      isLoading,
      isLoaded,
      isEmpty,

      // Pagination
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isStarting,
      isUpdating,
      isEnding,
      isRemoving,
      removingId,

      // Filter and sort
      filter,
      sortBy,

      // Core operations
      list,
      loadMore,
      get,
      getMany,
      start,
      update,
      end,
      remove,

      // Convenience methods
      refresh,
      select,
      clearSelection,
      reset,

      // Derived data helpers
      activeSessions,
      completedSessions,
      activeSessionForCar,

      // Error handling
      lastError,
      clearError,
    }),
    [
      sessions,
      selectedSession,
      isLoading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isStarting,
      isUpdating,
      isEnding,
      isRemoving,
      removingId,
      filter,
      sortBy,
      list,
      loadMore,
      get,
      getMany,
      start,
      update,
      end,
      remove,
      refresh,
      select,
      clearSelection,
      reset,
      activeSessions,
      completedSessions,
      activeSessionForCar,
      lastError,
      clearError,
    ]
  );
};